import DnsIcon from '@mui/icons-material/Dns';
import { Box, DialogContent, Stack } from '@mui/material';
import { CancelKeyConfirmOverlay } from './CancelKeyConfirmOverlay';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppModal } from '../../../../components/ui/AppModal';
import { useTheme } from '@mui/material/styles';
import { useFetch } from '../../../../hooks/useFetch';
import { InstanceApiFactory } from '../../api/instanceApi';
import { useInstanceKeys } from '../../hooks/useInstanceKeys';
import { useNotification } from '../../../notifications/hooks/useNotification';
import { InstanceDetailFooter } from './InstanceDetailFooter';
import { InstanceInfoSection } from '../form/InstanceInfoSection';
import { useInstanceInfoErrors } from '../form/useInstanceInfoErrors';
import type { InstanceInfoEditState } from '../form/InstanceInfoSection';
import { InstanceKeysSection } from './InstanceKeysSection';
import { InstanceMetaBadges } from './InstanceMetaBadges';
import type { InstanceRow } from '../../instanceTypes';
import { useGetTerrains } from '../../hooks/useGetTerrains';

interface InstanceDetailModalProps {
  instance: InstanceRow | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  onKeyRevoked?: () => void;
  onKeyGenerated?: () => void;
  /** No `modify` on the `instances` line: fields, save and key actions hidden (ADR-040 D5). */
  readOnly: boolean;
}

export function InstanceDetailModal({
  instance,
  open,
  onClose,
  onSaved,
  onKeyRevoked,
  onKeyGenerated,
  readOnly,
}: InstanceDetailModalProps) {
  const theme = useTheme();
  const { t } = useTranslation(['instances', 'common']);
  const [client] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);
  const { notifySuccess, notifyError } = useNotification();
  const { data: terrainList } = useGetTerrains();
  const terrainMap = useMemo(
    () => new Map(terrainList.map(t => [t.codeterrain, t])),
    [terrainList],
  );

  // Preserve last non-null instance so the Dialog close animation has content to render
  const [displayInstance, setDisplayInstance] = useState<InstanceRow | null>(instance);
  useEffect(() => {
    if (instance) setDisplayInstance(instance);
  }, [instance]);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Initialize form when modal opens or instance changes
  useEffect(() => {
    if (open && instance) {
      setName(instance.name ?? '');
      setEmail(instance.email ?? '');
      setTouched({});
    }
  }, [open, instance]);

  const formErrors = useInstanceInfoErrors(name, email);

  const handleSave = useCallback(async () => {
    if (!displayInstance) return;
    setTouched({ name: true, email: true });
    if (Object.values(formErrors).some(Boolean)) return;
    setSaving(true);
    try {
      await api.update(displayInstance.id, {
        instance_name: name.trim(),
        email: email.trim() || undefined,
      });
      notifySuccess(t('edit.saveSuccess'));
      onSaved?.();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  }, [displayInstance, formErrors, name, email, api, t, onSaved, notifySuccess, notifyError]);

  const {
    keys,
    keysLoading,
    generating,
    newKey,
    activeKeyCount,
    revoking,
    handleGenerate,
    handleRevoke,
    dismissNewKey,
    cancelConfirmOpen,
    handleConfirmCancel,
    handleDismissConfirm,
  } = useInstanceKeys({
    instance,
    open,
    api,
    onRevoked: onKeyRevoked,
    onGenerated: onKeyGenerated,
  });

  const editState: InstanceInfoEditState = {
    name,
    email,
    errors: formErrors,
    touched,
    onChange: (field, value) => {
      if (field === 'name') setName(value);
      else setEmail(value);
    },
    onBlur: field => setTouched(prev => ({ ...prev, [field]: true })),
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      icon={<DnsIcon sx={{ fontSize: theme.iconSize.sm }} />}
      iconBg={theme.palette.primary.main}
      title={displayInstance?.name || t('instances:detail.title')}
      subtitle={t(readOnly ? 'instances:detail.subtitleReadOnly' : 'instances:detail.subtitle')}
      headerSlot={
        displayInstance ? (
          <InstanceMetaBadges instance={displayInstance} activeKeyCount={activeKeyCount} />
        ) : undefined
      }
      maxWidth="md"
      titleId="instance-detail-title"
      closeLabel={t(readOnly ? 'detail.footer.close' : 'detail.footer.cancel')}
      paperSx={{
        borderRadius: theme.borderRadius.lg,
        overflow: 'hidden',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Relative wrapper so the confirm overlay can be positioned inside */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ── Screen 1: info + keys ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            filter: cancelConfirmOpen ? 'blur(3px) saturate(0.85)' : 'none',
            opacity: cancelConfirmOpen ? 0.5 : 1,
            transform: cancelConfirmOpen ? 'scale(0.985)' : 'scale(1)',
            transition:
              'filter 320ms ease, opacity 320ms ease, transform 320ms cubic-bezier(.2,.8,.2,1)',
            pointerEvents: cancelConfirmOpen ? 'none' : 'auto',
          }}
        >
          <DialogContent sx={{ px: 3, py: 2.5, overflowY: 'auto', flex: 1 }}>
            {displayInstance && (
              <Stack gap={2.5}>
                <InstanceInfoSection
                  instance={displayInstance}
                  editState={editState}
                  readOnly={readOnly}
                  terrainMap={terrainMap}
                />
                <InstanceKeysSection
                  keys={keys}
                  keysLoading={keysLoading}
                  generating={generating}
                  newKey={newKey}
                  revoking={revoking}
                  onGenerate={handleGenerate}
                  onRevoke={handleRevoke}
                  onDismissKey={dismissNewKey}
                  readOnly={readOnly}
                />
              </Stack>
            )}
          </DialogContent>

          <InstanceDetailFooter
            updatedAt={displayInstance?.updatedAt}
            onClose={onClose}
            onSave={handleSave}
            saving={saving}
            readOnly={readOnly}
          />
        </Box>

        <CancelKeyConfirmOverlay
          open={cancelConfirmOpen}
          revoking={revoking}
          onConfirm={handleConfirmCancel}
          onDismiss={handleDismissConfirm}
        />
      </Box>
    </AppModal>
  );
}
